export const SPRING_HOVER_SCALE = 1.05;
export const SPRING_HOVER_DURATION_S = 0.45;
export const SPRING_HOVER_BOUNCE = 0.12;

const ROOT_SELECTOR = "[data-spring-hover]";
const TARGET_SELECTOR = "[data-spring-hover-target]";
const MEGA_CARD_SELECTOR =
  ".custom-mega-collection-card, .custom-mega-product-card";
const MEDIA_CAROUSEL_CARD_SELECTOR = ".media-carousel__media-wrapper";
const MEDIA_CAROUSEL_TRIGGER_SELECTOR = ".media-carousel__card[data-spring-hover]";

/** @type {WeakSet<HTMLElement>} */
const boundTriggers = new WeakSet();
/** @type {WeakMap<HTMLElement, () => void>} */
const cleanups = new WeakMap();

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * @param {HTMLElement} root
 * @returns {HTMLElement | null}
 */
function findImageHoverTarget(root) {
  const explicit = root.querySelector(TARGET_SELECTOR);
  if (explicit instanceof HTMLElement) return explicit;

  const collectionImg = root.querySelector(".custom-mega-collection-card__img");
  if (collectionImg instanceof HTMLElement) return collectionImg;

  const productMedia = root.querySelector(".custom-mega-product-card__media");
  if (productMedia instanceof HTMLElement) return productMedia;

  const carouselMedia = root.querySelector(".media-carousel__media");
  if (carouselMedia instanceof HTMLElement) return carouselMedia;

  return null;
}

/**
 * @param {HTMLElement} el
 * @param {boolean} isCard
 */
function isCardHoverTarget(el) {
  return (
    el.matches(MEGA_CARD_SELECTOR) ||
    el.matches(MEDIA_CAROUSEL_CARD_SELECTOR)
  );
}

function setTransformOrigin(el, isCard) {
  el.style.transformOrigin = isCard ? "center center" : "50% 60%";
}

/**
 * @param {HTMLElement | HTMLElement[]} targets
 * @returns {HTMLElement[]}
 */
function normalizeTargets(targets) {
  const list = Array.isArray(targets) ? targets : [targets];
  return [...new Set(list.filter((el) => el instanceof HTMLElement))];
}

/**
 * @param {HTMLElement} trigger
 * @param {HTMLElement | HTMLElement[]} targets
 */
export function bindSpringHover(trigger, targets) {
  if (boundTriggers.has(trigger)) return;

  const hoverTargets = normalizeTargets(targets);
  if (hoverTargets.length === 0) return;

  boundTriggers.add(trigger);

  hoverTargets.forEach((target) => {
    setTransformOrigin(target, isCardHoverTarget(target));
  });

  /** @type {Array<(() => void) | void>} */
  let hoverInAnims = [];
  /** @type {Array<(() => void) | void>} */
  let hoverOutAnims = [];

  const cancelAnims = (anims) => {
    anims.forEach((stop) => {
      if (typeof stop === "function") stop();
    });
  };

  const onEnter = () => {
    if (prefersReducedMotion()) {
      hoverTargets.forEach((target) => {
        target.style.transform = `scale(${SPRING_HOVER_SCALE})`;
      });
      return;
    }

    import("@theme/motion")
      .then(({ animate }) => {
        cancelAnims(hoverOutAnims);
        hoverOutAnims = [];
        hoverInAnims = hoverTargets.map((target) =>
          animate(
            target,
            { scale: SPRING_HOVER_SCALE },
            {
              type: "spring",
              duration: SPRING_HOVER_DURATION_S,
              bounce: SPRING_HOVER_BOUNCE,
            },
          ),
        );
      })
      .catch(() => {
        hoverTargets.forEach((target) => {
          target.style.transform = `scale(${SPRING_HOVER_SCALE})`;
        });
      });
  };

  const onLeave = () => {
    if (prefersReducedMotion()) {
      hoverTargets.forEach((target) => {
        target.style.transform = "none";
      });
      return;
    }

    import("@theme/motion")
      .then(({ animate }) => {
        cancelAnims(hoverInAnims);
        hoverInAnims = [];
        hoverOutAnims = hoverTargets.map((target) =>
          animate(
            target,
            { scale: 1 },
            {
              type: "spring",
              duration: SPRING_HOVER_DURATION_S,
              bounce: SPRING_HOVER_BOUNCE,
            },
          ),
        );
      })
      .catch(() => {
        hoverTargets.forEach((target) => {
          target.style.transform = "none";
        });
      });
  };

  trigger.addEventListener("mouseenter", onEnter);
  trigger.addEventListener("mouseleave", onLeave);
  trigger.addEventListener("focusin", onEnter);
  trigger.addEventListener("focusout", onLeave);

  cleanups.set(trigger, () => {
    trigger.removeEventListener("mouseenter", onEnter);
    trigger.removeEventListener("mouseleave", onLeave);
    trigger.removeEventListener("focusin", onEnter);
    trigger.removeEventListener("focusout", onLeave);
    boundTriggers.delete(trigger);
  });
}

/**
 * @param {HTMLElement} card
 * @returns {HTMLElement[]}
 */
function getMegaCardHoverTargets(card) {
  /** @type {HTMLElement[]} */
  const targets = [card];
  const imageTarget = findImageHoverTarget(card);
  if (imageTarget && imageTarget !== card) targets.push(imageTarget);
  return targets;
}

/**
 * @param {HTMLElement} card
 * @returns {HTMLElement[]}
 */
function getMediaCarouselHoverTargets(card) {
  /** @type {HTMLElement[]} */
  const targets = [];
  const wrapper = card.querySelector(MEDIA_CAROUSEL_CARD_SELECTOR);
  const media = card.querySelector(".media-carousel__media");
  if (wrapper instanceof HTMLElement) targets.push(wrapper);
  if (media instanceof HTMLElement && media !== wrapper) targets.push(media);
  return targets;
}

/**
 * @param {ParentNode} root
 */
export function initSpringHover(root = document) {
  root.querySelectorAll(ROOT_SELECTOR).forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (el.closest(".menu-drawer")) return;

    if (el.matches(MEGA_CARD_SELECTOR)) {
      bindSpringHover(el, getMegaCardHoverTargets(el));
      return;
    }

    if (el.matches(MEDIA_CAROUSEL_TRIGGER_SELECTOR)) {
      bindSpringHover(el, getMediaCarouselHoverTargets(el));
      return;
    }

    const imageTarget = findImageHoverTarget(el);
    if (imageTarget) bindSpringHover(el, [imageTarget]);
  });
}

function whenReady(fn) {
  if (document.readyState !== "loading") fn();
  else document.addEventListener("DOMContentLoaded", fn);
}

whenReady(() => {
  requestAnimationFrame(() => initSpringHover());
});

document.addEventListener("shopify:section:load", (event) => {
  const target = event.target;
  if (target instanceof Element) initSpringHover(target);
});

document.addEventListener("shopify:section:unload", (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  target.querySelectorAll(ROOT_SELECTOR).forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    const cleanup = cleanups.get(el);
    if (typeof cleanup === "function") cleanup();
    cleanups.delete(el);
  });
});
